/**
 * Pure edits to the quiz draft and to AI-generated draft questions.
 * Any edit to the quiz configuration drops the earlier approval preview, so the
 * user always approves exactly what will be created.
 */

function invalidate(draft) {
  return {
    ...draft,
    awaiting_confirmation: false,
    confirmation_fingerprint: null,
    locked_question_refs: [],
  };
}

/** Store the bank pick the professor reviewed (does not clear confirmation). */
export function setLockedQuestionRefs(draft, refs = []) {
  const locked = Array.isArray(refs)
    ? refs
        .map((r) => ({
          bank: r?.bank === "microlab" ? "microlab" : "private",
          id: String(r?.id || "").trim(),
        }))
        .filter((r) => r.id)
    : [];
  return { ...draft, locked_question_refs: locked };
}

export function updateQuizSettings(draft, patch = {}) {
  const next = { ...draft };
  if (patch.title !== undefined) next.title = String(patch.title || "").slice(0, 200);
  if (patch.time_limit_minutes !== undefined) {
    const mins = Math.round(Number(patch.time_limit_minutes));
    next.time_limit_minutes = Number.isFinite(mins) && mins > 0 ? Math.min(300, mins) : 30;
  }
  ["shuffle_questions", "shuffle_answers", "show_score_immediately", "show_answers_after"].forEach(
    (key) => {
      if (patch[key] !== undefined) next[key] = !!patch[key];
    }
  );
  if (patch.instructions !== undefined) {
    const text = String(patch.instructions || "").trim();
    next.rules_html = text
      ? text
          .split(/\n+/)
          .map((line) => `<p>${line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`)
          .join("")
      : draft.rules_html;
  }
  return invalidate(next);
}

export function instructionsText(rulesHtml) {
  return String(rulesHtml || "")
    .replace(/<\/(p|li|div|h\d)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

export function updateTopicRule(draft, index, patch = {}) {
  const rules = [...(draft.topic_rules || [])];
  if (!rules[index]) return draft;
  const rule = { ...rules[index] };
  if (patch.num_questions !== undefined) {
    rule.num_questions = Math.max(1, Math.min(50, Math.round(Number(patch.num_questions)) || 1));
  }
  if (patch.difficulty_filter !== undefined) {
    rule.difficulty_filter = ["easy", "medium", "hard"].includes(patch.difficulty_filter)
      ? patch.difficulty_filter
      : "";
  }
  rules[index] = rule;
  return invalidate({ ...draft, topic_rules: rules });
}

export function removeTopicRule(draft, index) {
  const rules = (draft.topic_rules || []).filter((_, i) => i !== index);
  return invalidate({ ...draft, topic_rules: rules });
}

export function totalQuestions(draft) {
  return (draft?.topic_rules || []).reduce((sum, r) => sum + (Number(r.num_questions) || 0), 0);
}

/* ---- AI-generated draft questions (pending_generated_questions) ---- */

function withQuestions(draft, questions) {
  const pending = draft.pending_generated_questions;
  if (!pending) return draft;
  if (!questions.length) return { ...draft, pending_generated_questions: null };
  return {
    ...draft,
    pending_generated_questions: {
      ...pending,
      questions,
      // The backend compares this to what it saves; refresh it on every edit.
      fingerprint: `edit-${Date.now().toString(36)}-${questions.length}`,
    },
  };
}

export function updateGeneratedQuestion(draft, index, patch = {}) {
  const list = [...(draft.pending_generated_questions?.questions || [])];
  if (!list[index]) return draft;
  const q = { ...list[index] };
  if (patch.question_content !== undefined) q.question_content = String(patch.question_content || "");
  if (patch.title !== undefined) q.title = String(patch.title || "");
  if (patch.explanation !== undefined) q.explanation = String(patch.explanation || "");
  if (patch.difficulty !== undefined && ["easy", "medium", "hard"].includes(patch.difficulty)) {
    q.difficulty = patch.difficulty;
  }
  if (Array.isArray(patch.answers)) {
    const answers = patch.answers.slice(0, 4).map((a) => ({
      answer_text: String(a?.answer_text || "").trim(),
      is_correct: !!a?.is_correct,
    }));
    while (answers.length < 4) answers.push({ answer_text: "", is_correct: false });
    q.answers = answers;
  }
  if (patch.remove_image) q.question_image_url = null;
  list[index] = q;
  return withQuestions(draft, list);
}

export function removeGeneratedQuestion(draft, index) {
  const list = (draft.pending_generated_questions?.questions || []).filter((_, i) => i !== index);
  return withQuestions(draft, list);
}

export function questionProblems(q) {
  const problems = [];
  if (!String(q?.question_content || q?.title || "").trim()) problems.push("Question text is empty.");
  const answers = Array.isArray(q?.answers) ? q.answers : [];
  if (answers.filter((a) => String(a.answer_text || "").trim()).length < 2) {
    problems.push("Add at least two answers.");
  }
  if (answers.filter((a) => a.is_correct).length !== 1) problems.push("Mark exactly one correct answer.");
  return problems;
}
